// PandaPanla Admin Platform API Service

const _envUrl = import.meta.env.VITE_API_BASE_URL;
const API_BASE_URL = (_envUrl && _envUrl.length > 0) ? _envUrl : (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.hostname + ':3000' : 'http://localhost:3000');

// 获取认证 token
const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// 通用请求封装
const request = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = { ...getAuthHeaders(), ...options.headers };

  const response = await fetch(url, { ...options, headers });
  const data = await response.json();

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('accessToken');
      window.location.href = '/login';
    }
    throw new Error(data.error || 'Request failed');
  }

  return data;
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => request('/admin/stats'),
  getTenants: () => request('/admin/tenants'),
  getUsers: () => request('/admin/users'),
  getSkills: (status?: string) => request(`/admin/skills${status ? `?status=${status}` : ''}`),
};

// Reports API
export const reportsAPI = {
  getApiUsage: (period?: string) => request(`/reports/api-usage?period=${period || '7days'}`),
  getRevenue: (period?: string) => request(`/reports/revenue?period=${period || '7days'}`),
  getResourceUsage: (period?: string) => request(`/reports/resource-usage?period=${period || '7days'}`),
  getUserActivity: (period?: string) => request(`/reports/user-activity?period=${period || '7days'}`),
  getTenantSummary: (period?: string) => request(`/reports/tenant-summary?period=${period || '7days'}`),
};

// Audit Logs API
export const auditLogsAPI = {
  list: (params?: {
    page?: number;
    limit?: number;
    action?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.action && params.action !== 'all') searchParams.set('action', params.action);
    if (params?.userId) searchParams.set('userId', params.userId);
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    if (params?.search) searchParams.set('search', params.search);
    const qs = searchParams.toString();
    return request(`/admin/audit-logs${qs ? '?' + qs : ''}`);
  },
};

// Tenant API
export const tenantsAPI = {
  list: () => request('/admin/tenants'),
  get: (id: string) => request(`/admin/tenants/${id}`),
  create: (data: any) => request('/admin/tenants', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request(`/admin/tenants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request(`/admin/tenants/${id}`, { method: 'DELETE' }),
};

// User API
export const usersAPI = {
  list: () => request('/admin/users'),
  get: (id: string) => request(`/admin/users/${id}`),
  updateStatus: (id: string, status: string) => request(`/admin/users/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status })
  }),
  create: (data: any) => request('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateRole: (id: string, role: string) => request(`/admin/users/${id}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
  delete: (id: string) => request(`/admin/users/${id}`, { method: 'DELETE' }),
};

// Skill API
export const skillsAPI = {
  list: (status?: string) => request(`/admin/skills${status ? `?status=${status}` : ''}`),
  updateStatus: (id: string, status: string, visibility?: string) =>
    request(`/admin/skills/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, visibility })
    }),
};

// Plans API
export const plansAPI = {
  list: () => request('/admin/plans'),
  stats: () => request('/admin/plans/stats'),
  create: (data: any) => request('/admin/plans', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request(`/admin/plans/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request(`/admin/plans/${id}`, { method: 'DELETE' }),
};

// Configs API
export const configsAPI = {
  list: () => request('/configs'),
  update: (id: string, data: { configValue?: string; isActive?: boolean }) =>
    request(`/configs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  create: (data: any) => request('/configs', { method: 'POST', body: JSON.stringify(data) }),
  delete: (id: string) => request(`/configs/${id}`, { method: 'DELETE' }),
  llmUsage: () => request('/configs/llm-usage'),
};

// Workers API
export const workersAPI = {
  list: () => request('/admin/workers'),
  toggle: (workerId: string, action: 'start' | 'stop') =>
    request('/admin/workers/toggle', { method: 'POST', body: JSON.stringify({ workerId, action }) }),
};

// Invites API
export const invitesAPI = {
  list: () => request('/admin/invites'),
  generate: (data: { count: number; maxUses: number; inviteQuota: number; expiryDays: number | null; note?: string }) =>
    request('/admin/invites', { method: 'POST', body: JSON.stringify(data) }),
  deactivate: (id: string) => request(`/admin/invites/${id}/deactivate`, { method: 'PUT' }),
};

// Exchange Items API
export const exchangeItemsAPI = {
  list: () => request('/admin/exchange-items'),
  create: (data: any) => request('/admin/exchange-items', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request(`/admin/exchange-items/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request(`/admin/exchange-items/${id}`, { method: 'DELETE' }),
};

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => {
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
  },
};

export default request;
